import { useRef } from 'react';
import { DEFAULT_NOISE_SUPPRESSION, type NoiseSuppression } from '@neosocial/protocol';
import { create } from 'zustand';
import { storage } from '../lib/storage';
import { initialVoice, voiceReducer, type VoiceAction, type VoiceSession } from './machine';

export type InputMode = 'va' | 'ptt';

export interface VoicePrefs {
  inputMode: InputMode;
  /** KeyboardEvent.code */
  pttKey: string;
  /** How long the gate stays open after release (ms). */
  pttReleaseMs: number;
  /** Keybinds (voice/hotkeys.ts): "Ctrl+Shift+KeyM", or null when cleared. */
  muteKey: string | null;
  deafenKey: string | null;
  /** Voice activity threshold, 0..1 input level. */
  sensitivity: number;
  inputDeviceId: string | null;
  outputDeviceId: string | null;
  /**
   * The master output volume, 0..2 (Discord's "Output Volume", the output
   * menu on deafen): every voice and stream sound plays at its own volume
   * times this.
   */
  outputVolume: number;
  /** The camera for video in voice (null: the default, the front one on a phone). */
  cameraDeviceId: string | null;
  /** Noise suppression (Settings › Voice, 2026-11-01): Off, Standard (default) or Strong. */
  noiseSuppression: NoiseSuppression;
  /** What the dock's quick noise toggle turns back on: the last choice that was not Off. */
  noiseOnMode: Exclude<NoiseSuppression, 'off'>;
  /**
   * The priority-speaker key (KeyboardEvent.code or a mouse side button, as
   * push-to-talk's), held to talk as a priority speaker; null: not bound
   * (Discord binds none by default).
   */
  priorityKey: string | null;
}

const DEFAULT_PREFS: VoicePrefs = {
  inputMode: 'va',
  pttKey: 'KeyV',
  pttReleaseMs: 200,
  // Discord's defaults
  muteKey: 'Ctrl+Shift+KeyM',
  deafenKey: 'Ctrl+Shift+KeyD',
  sensitivity: 0.06,
  inputDeviceId: null,
  outputDeviceId: null,
  outputVolume: 1,
  cameraDeviceId: null,
  noiseSuppression: DEFAULT_NOISE_SUPPRESSION,
  noiseOnMode: 'standard',
  priorityKey: null,
};

interface VoiceStore {
  session: VoiceSession;
  prefs: VoicePrefs;
  act(a: VoiceAction): void;
  setPrefs(p: Partial<VoicePrefs>): void;
}

const kept = storage.getJson<Partial<VoiceSession>>('voice-local', {});

export const useVoice = create<VoiceStore>((set) => ({
  session: initialVoice({
    localMutes: kept.localMutes ?? {},
    volumes: kept.volumes ?? {},
    selfMute: kept.selfMute ?? false,
    selfDeaf: kept.selfDeaf ?? false,
  }),
  prefs: { ...DEFAULT_PREFS, ...storage.getJson<Partial<VoicePrefs>>('voice-prefs', {}) },
  act(a) {
    set((st) => {
      const session = voiceReducer(st.session, a);
      if (a.type === 'local_mute' || a.type === 'volume' || a.type === 'self') {
        storage.setJson('voice-local', {
          localMutes: session.localMutes,
          volumes: session.volumes,
          selfMute: session.selfMute,
          selfDeaf: session.selfDeaf,
        });
      }
      return session === st.session ? st : { session };
    });
  },
  setPrefs(p) {
    set((st) => {
      const prefs = { ...st.prefs, ...p };
      storage.setJson('voice-prefs', prefs);
      return { prefs };
    });
  },
}));

export function voiceSession(): VoiceSession {
  return useVoice.getState().session;
}

/**
 * Every field the same object but `talking` and `priority`: only someone's
 * talk ring (or priority-speaker badge, which reads its own selector) changed.
 */
export function onlyRingsChanged(a: VoiceSession, b: VoiceSession): boolean {
  for (const k in b) {
    if (k === 'talking' || k === 'priority') continue;
    if (a[k as keyof VoiceSession] !== b[k as keyof VoiceSession]) return false;
  }
  return true;
}

/**
 * The session for a screen that reads all of it but draws no talk ring
 * itself (the voice room's frame, the dock's voice row): a change of
 * `talking` alone keeps the session it had, so a speaking edge (several a
 * second while people talk) re-renders only the rings, which read
 * `userSpeaking` through their own selector (voice performance, 2026-11-01).
 * `talking` in what it returns may be stale: never draw a ring from it.
 */
export function useVoiceQuiet(): VoiceSession {
  const last = useRef<VoiceSession | null>(null);
  return useVoice((v) => {
    const prev = last.current;
    if (prev && (prev === v.session || onlyRingsChanged(prev, v.session))) return prev;
    last.current = v.session;
    return v.session;
  });
}
